<template>
	<div class="advertisement-settings">
		<!-- Header -->
		<div class="advertisement-settings-header">
			<h3 class="main-label">
				Advertisements
				<i id="advertisement-settings-info" class="far fa-info-circle sys-icon"></i>
				<b-tooltip target="advertisement-settings-info" triggers="hover">
					Shown to students while active. Use Preview to see where.
				</b-tooltip>
			</h3>
			<div class="advertisement-settings-actions">
				<template v-if="isEditing">
					<button id="advertisement-discard" type="button" class="advertisement-action-button cancel" @click="discardChanges">
						<img src="@/assets/images/cancel-icon-red.svg" alt="Cancel" />
					</button>
					<b-tooltip target="advertisement-discard" triggers="hover" placement="top">Cancel</b-tooltip>
					<template v-if="hasUnsavedChanges">
						<button id="advertisement-save" type="button" class="advertisement-action-button save" @click="saveAdvertisements">
							<img src="@/assets/images/check-icon-green.svg" alt="Save" />
						</button>
						<b-tooltip target="advertisement-save" triggers="hover" placement="top">Save</b-tooltip>
					</template>
				</template>
				<template v-else>
					<button id="advertisement-edit" type="button" class="advertisement-action-button edit" @click="isEditing = true">
						<img src="@/assets/images/edit-icon.svg" alt="Edit" />
					</button>
					<b-tooltip target="advertisement-edit" triggers="hover" placement="top">Edit</b-tooltip>
				</template>
			</div>
		</div>

		<b-tabs content-class="pt-3" lazy>
			<!-- Settings tab -->
			<b-tab title="Settings" active>
				<div class="advertisement-settings-groups">
					<div>
						<div class="advertisement-settings-group-title">
							Standard cards
							<i id="advertisement-standard-info" class="far fa-info-circle"></i>
							<b-tooltip target="advertisement-standard-info" triggers="hover">Student Dashboard and course pages</b-tooltip>
						</div>
						<div class="advertisement-settings-fields">
							<label class="advertisement-settings-field">
								<span class="advertisement-settings-field-label">Per view <span v-if="changedSettings.includes('slidesToShow')" class="advertisement-edited-dot"></span></span>
								<input v-if="isEditing" v-model.number="slidesToShow" type="number" min="1" step="1" class="form-input" />
								<span v-else class="advertisement-settings-value">{{ advertisementsToSave.slidesToShow }}</span>
							</label>
							<label class="advertisement-settings-field">
								<span class="advertisement-settings-field-label">Height <span v-if="changedSettings.includes('height')" class="advertisement-edited-dot"></span></span>
								<div v-if="isEditing" class="advertisement-size-input">
									<input v-model.number="heightSize" type="number" min="1" class="form-input" placeholder="Auto" />
									<select v-model="heightUnit" class="form-input">
										<option v-for="sizeUnit in sizeUnits" :key="sizeUnit" :value="sizeUnit">{{ sizeUnit }}</option>
									</select>
								</div>
								<span v-else class="advertisement-settings-value">{{ heightSize || "Auto" }}<small v-if="heightSize">{{ heightUnit }}</small></span>
							</label>
						</div>
					</div>
					<div>
						<div class="advertisement-settings-group-title">
							Compact cards
							<i id="advertisement-compact-info" class="far fa-info-circle"></i>
							<b-tooltip target="advertisement-compact-info" triggers="hover">Narrow spots such as the ebook course header and sidebar</b-tooltip>
						</div>
						<div class="advertisement-settings-fields">
							<label class="advertisement-settings-field">
								<span class="advertisement-settings-field-label">Height <span v-if="changedSettings.includes('compactHeight')" class="advertisement-edited-dot"></span></span>
								<div v-if="isEditing" class="advertisement-size-input">
									<input v-model.number="compactHeightSize" type="number" min="1" class="form-input" placeholder="Auto" />
									<select v-model="compactHeightUnit" class="form-input">
										<option v-for="sizeUnit in sizeUnits" :key="sizeUnit" :value="sizeUnit">{{ sizeUnit }}</option>
									</select>
								</div>
								<span v-else class="advertisement-settings-value">{{ compactHeightSize || "Auto" }}<small v-if="compactHeightSize">{{ compactHeightUnit }}</small></span>
							</label>
						</div>
					</div>
					<div>
						<div class="advertisement-settings-group-title">Slideshow</div>
						<div class="advertisement-settings-fields">
							<label class="advertisement-settings-field">
								<span class="advertisement-settings-field-label">Autoplay <span v-if="changedSettings.includes('autoplay')" class="advertisement-edited-dot"></span></span>
								<span class="advertisement-settings-value">
									<span v-if="isEditing" class="form-check form-switch m-0">
										<input v-model="autoplay" class="form-check-input" type="checkbox" role="switch" />
									</span>
									<template v-else>{{ autoplay ? "On" : "Off" }}</template>
								</span>
							</label>
							<label class="advertisement-settings-field">
								<span class="advertisement-settings-field-label">Interval <span v-if="changedSettings.includes('autoplaySpeed')" class="advertisement-edited-dot"></span></span>
								<div v-if="isEditing" class="advertisement-size-input">
									<input v-model.number="autoplaySpeed" type="number" min="1" class="form-input" :disabled="!autoplay" />
									<small class="advertisement-settings-suffix">ms</small>
								</div>
								<span v-else class="advertisement-settings-value">{{ advertisementsToSave.autoplaySpeed }}<small>ms</small></span>
							</label>
						</div>
					</div>
				</div>

				<div v-if="showEmptyList" class="advertisement-empty-state">
					<span class="advertisement-empty-state-icon">
						<svg viewBox="0 0 24 24" aria-hidden="true">
							<path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6l-5 4H4a1 1 0 0 0-1 1z" />
							<path d="M15.5 8.5a5 5 0 0 1 0 7" />
							<path d="M18.5 5.5a9 9 0 0 1 0 13" />
						</svg>
					</span>
					<p class="advertisement-empty-state-title">No advertisements yet</p>
					<p class="advertisement-empty-state-text">Add one to promote courses and offers to students.</p>
				</div>

				<!-- Tiles -->
				<div class="advertisement-list">
					<draggable v-model="slides" class="advertisement-list-items" :class="{ editing: isEditing }" handle=".advertisement-tile-thumbnail" :disabled="!isEditing">
						<div v-for="(slide, index) in slides" :key="index" class="advertisement-tile" :class="{ inactive: slide.isActive === false }">
							<div class="advertisement-tile-thumbnail" :title="isEditing ? 'Drag to reorder' : null">
								<img v-if="slide.image && !slide.html" :src="slide.image" alt="" />
								<i v-else class="fas fa-code"></i>
								<span v-if="slide.isActive === false" class="advertisement-tile-badge">Inactive</span>
								<div v-if="isEditing" class="advertisement-tile-menu">
									<a v-if="IS_HTML_ADVERTISEMENT_ENABLED || !slide.html" role="button" @click="openModal(index)">
										<img src="@/assets/images/edit-icon.svg" alt="Edit" />
									</a>
									<a role="button" @click="confirmDelete(index)">
										<img src="@/assets/images/delete-icon.svg" alt="Delete" />
									</a>
								</div>
							</div>
							<p class="advertisement-tile-title">{{ slide.title || (slide.html ? "HTML advertisement" : "Image advertisement") }}</p>
							<div class="advertisement-tile-footer">
								<span>{{ targetLabel(slide) }}</span>
								<div class="form-check form-switch m-0" title="Active">
									<input class="form-check-input" type="checkbox" role="switch" :checked="slide.isActive !== false" :disabled="!isEditing" @change="toggleActive(index)" />
								</div>
							</div>
						</div>
						<template #footer>
							<div v-if="isEditing" class="advertisement-tile add-new" @click="openModal(null)">
								<i class="far fa-plus-square"></i>
								Add New Advertisement
							</div>
						</template>
					</draggable>
				</div>
			</b-tab>

			<!-- Preview tab -->
			<b-tab title="Preview">
				<div class="advertisement-settings-groups advertisement-preview-pickers">
					<label class="advertisement-settings-field advertisement-preview-page">
						<span class="advertisement-settings-field-label">Page</span>
						<select v-model="previewPage" class="form-input">
							<option :value="PreviewPage.STUDENT_DASHBOARD">Student Dashboard</option>
							<option :value="PreviewPage.COURSE">Course page</option>
						</select>
					</label>
					<label v-if="isCoursePreview" class="advertisement-settings-field advertisement-preview-course">
						<span class="advertisement-settings-field-label">Course</span>
						<v-select
							v-model="previewProductVariantId"
							class="form-input"
							:options="courseOptions"
							:reduce="(option) => option.id"
							label="label"
							placeholder="Search by ID or title"
						/>
					</label>
				</div>

				<div v-if="previewSlides.length === 0" class="advertisement-empty-state">
					<span class="advertisement-empty-state-icon">
						<svg v-if="isPreviewCourseMissing" viewBox="0 0 24 24" aria-hidden="true">
							<circle cx="11" cy="11" r="7" />
							<path d="M20 20l-3.5-3.5" />
						</svg>
						<svg v-else viewBox="0 0 24 24" aria-hidden="true">
							<path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6l-5 4H4a1 1 0 0 0-1 1z" />
							<path d="M15.5 8.5a5 5 0 0 1 0 7" />
							<path d="M18.5 5.5a9 9 0 0 1 0 13" />
						</svg>
					</span>
					<p class="advertisement-empty-state-title">{{ isPreviewCourseMissing ? "Pick a course" : "Nothing to show here" }}</p>
					<p class="advertisement-empty-state-text">{{ isPreviewCourseMissing ? "See which advertisements its students get." : "No active advertisement targets this page." }}</p>
				</div>
				<div v-else class="advertisement-preview-frame" :class="{ compact: isCompactPreview, 'with-arrows': hasPreviewArrows }">
					<AdvertisementCarousel
						:key="previewKey"
						:advertisements="previewAdvertisements"
						:productVariantId="isCoursePreview ? previewProductVariantId : null"
						:compact="isCompactPreview"
						preview
					/>
				</div>
			</b-tab>
		</b-tabs>

		<AddEditAdvertisementModal
			:visible="showModal"
			:advertisement="editingIndex === null ? null : slides[editingIndex]"
			:settings="{ height, compactHeight }"
			:courseOptions="courseOptions"
			@close="showModal = false"
			@save="saveSlide"
		/>
	</div>
</template>

<script>
import draggable from "vuedraggable";
import { mapActions, mapGetters, mapState } from "vuex";
import AddEditAdvertisementModal from "./AddEditAdvertisementModal.vue";
import AdvertisementCarousel from "../../../Advertisement/AdvertisementCarousel.vue";
import { DEFAULT_AUTOPLAY_SPEED, DEFAULT_SLIDES_TO_SHOW, IS_HTML_ADVERTISEMENT_ENABLED, PreviewPage } from "../../../../constants/advertisement";

export default {
	name: "AdvertisementSettings",
	components: { draggable, AddEditAdvertisementModal, AdvertisementCarousel },
	data() {
		const sizeUnits = ["rem", "px"];

		return {
			// Settings
			slides: [],
			slidesToShow: DEFAULT_SLIDES_TO_SHOW,
			heightSize: null,
			heightUnit: sizeUnits[0],
			compactHeightSize: null,
			compactHeightUnit: sizeUnits[0],
			autoplay: true,
			autoplaySpeed: DEFAULT_AUTOPLAY_SPEED,
			sizeUnits,
			isEditing: false,
			savedAdvertisements: "",
			// Modal
			showModal: false,
			editingIndex: null,
			// Preview
			previewPage: PreviewPage.STUDENT_DASHBOARD,
			previewProductVariantId: null,
			PreviewPage,
			IS_HTML_ADVERTISEMENT_ENABLED,
		};
	},
	computed: {
		...mapState("appState", ["systemParameters"]),
		...mapGetters("productVariant", ["allProductVariants"]),

		// Settings
		height() {
			return this.heightSize > 0 ? `${this.heightSize}${this.heightUnit}` : null;
		},
		compactHeight() {
			return this.compactHeightSize > 0 ? `${this.compactHeightSize}${this.compactHeightUnit}` : null;
		},
		advertisementsToSave() {
			const advertisements = {
				slidesToShow: this.slidesToShow > 0 ? this.slidesToShow : DEFAULT_SLIDES_TO_SHOW,
				autoplay: this.autoplay,
				autoplaySpeed: this.autoplaySpeed > 0 ? this.autoplaySpeed : DEFAULT_AUTOPLAY_SPEED,
				slides: this.slides,
			};
			if (this.height) {
				advertisements.height = this.height;
			}
			if (this.compactHeight) {
				advertisements.compactHeight = this.compactHeight;
			}

			return advertisements;
		},
		// Compared with the value last loaded or saved
		hasUnsavedChanges() {
			return JSON.stringify(this.advertisementsToSave) !== this.savedAdvertisements;
		},
		changedSettings() {
			const savedSettings = JSON.parse(this.savedAdvertisements || "{}");

			return ["slidesToShow", "height", "compactHeight", "autoplay", "autoplaySpeed"].filter((key) => savedSettings[key] !== this.advertisementsToSave[key]);
		},
		showEmptyList() {
			return this.slides.length === 0 && !this.isEditing;
		},
		courseOptions() {
			return this.allProductVariants.map((variant) => ({
				id: String(variant.id),
				status: (variant.status || "").toLowerCase(),
				isEbook: variant.properties?.contentViewTheme?.toLowerCase() === "ebook",
				label: `#${variant.id} ${[variant.product?.title, variant.title].filter(Boolean).join(" - ")}`,
			}));
		},

		// Preview
		isCoursePreview() {
			return this.previewPage === PreviewPage.COURSE;
		},
		isPreviewCourseMissing() {
			return this.isCoursePreview && !this.previewProductVariantId;
		},
		// Ebook courses show ads in the narrow sidebar
		isCompactPreview() {
			return this.isCoursePreview && this.courseOptions.some((option) => option.id === this.previewProductVariantId && option.isEbook);
		},
		previewSlides() {
			const activeSlides = this.slides.filter((slide) => slide.isActive !== false);
			if (!this.isCoursePreview) {
				return activeSlides.filter((slide) => slide.showOnStudentDashboard !== false);
			}

			return activeSlides.filter((slide) => [].concat(slide.productVariantIds || []).map(String).includes(this.previewProductVariantId));
		},
		previewAdvertisements() {
			return { ...this.advertisementsToSave, slides: this.previewSlides };
		},
		hasPreviewArrows() {
			return !this.isCompactPreview && this.previewSlides.length > this.advertisementsToSave.slidesToShow;
		},
		// The carousel does not pick up changes, so it is rebuilt whenever what it shows changes
		previewKey() {
			return JSON.stringify([this.previewPage, this.previewProductVariantId, this.previewAdvertisements]);
		},
	},
	created() {
		this.loadAdvertisements();
		// The admin header only loads batches on course pages
		if (this.allProductVariants.length === 0) {
			this.fetchProductVariants();
		}
	},
	methods: {
		...mapActions("appState", ["editSystemParameters", "fetchSystemParameters"]),
		...mapActions("productVariant", ["fetchProductVariants"]),

		// Load and save
		loadAdvertisements() {
			const advertisements = this.systemParameters?.Advertisements || {};

			this.slides = JSON.parse(JSON.stringify(advertisements.slides || [])).map((slide) => ({
				...slide,
				isActive: String(slide.isActive).toLowerCase() !== "false",
				showOnStudentDashboard: String(slide.showOnStudentDashboard).toLowerCase() !== "false",
			}));
			this.slidesToShow = Number(advertisements.slidesToShow) || DEFAULT_SLIDES_TO_SHOW;
			[this.heightSize, this.heightUnit] = this.splitSize(advertisements.height);
			[this.compactHeightSize, this.compactHeightUnit] = this.splitSize(advertisements.compactHeight);
			this.autoplay = String(advertisements.autoplay).toLowerCase() !== "false";
			this.autoplaySpeed = Number(advertisements.autoplaySpeed) || DEFAULT_AUTOPLAY_SPEED;
			this.savedAdvertisements = JSON.stringify(this.advertisementsToSave);
		},
		splitSize(size) {
			const match = /^(\d*\.?\d+)(rem|px)$/i.exec(size || "");

			return match ? [Number(match[1]), match[2].toLowerCase()] : [null, this.sizeUnits[0]];
		},
		saveAdvertisements() {
			const parameterValue = JSON.stringify(this.advertisementsToSave);

			return this.editSystemParameters({ parameterName: "Advertisements", parameterValue }).then(() => {
				this.savedAdvertisements = parameterValue;
				this.isEditing = false;
				return this.fetchSystemParameters();
			});
		},
		discardChanges() {
			this.loadAdvertisements();
			this.isEditing = false;
		},

		// Tiles
		targetLabel(slide) {
			const batchCount = [].concat(slide.productVariantIds || []).length;
			const batchLabel = `${batchCount} ${batchCount === 1 ? "batch" : "batches"}`;
			if (slide.showOnStudentDashboard === false) {
				return `Course pages only, ${batchLabel}`;
			}

			return batchCount === 0 ? "Student Dashboard only" : `Student Dashboard + ${batchLabel}`;
		},
		openModal(index) {
			this.editingIndex = index;
			this.showModal = true;
		},
		saveSlide(slide) {
			if (this.editingIndex === null) {
				this.slides.push(slide);
			} else {
				this.slides.splice(this.editingIndex, 1, slide);
			}

			this.showModal = false;
		},
		toggleActive(index) {
			const slide = this.slides[index];

			this.slides.splice(index, 1, { ...slide, isActive: slide.isActive === false });
		},
		async confirmDelete(index) {
			const isConfirmed = await this.$bvModal.msgBoxConfirm(
				`Are you sure you want to delete the advertisement "${this.slides[index].title || "Untitled"}"?`,
				{
					title: "Confirm Advertisement Deletion",
					okTitle: "Yes, Delete",
					cancelTitle: "Cancel",
					centered: true,
					size: "md",
					okVariant: "danger",
				}
			);
			if (isConfirmed) {
				this.slides.splice(index, 1);
			}
		},
	},
};
</script>

<style scoped>
/* Header */
.advertisement-settings .main-label {
	font-weight: 500;
	font-size: 1rem;
	line-height: 1.37;
	text-transform: uppercase;
	color: #727272;
	margin-bottom: 1rem;
}
.advertisement-settings-header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
}
.advertisement-settings-actions {
	display: flex;
	gap: 0.5rem;
}
.advertisement-action-button {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 1.625rem;
	height: 1.625rem;
	padding: 0;
	border: 1px solid #cbcbcb;
	border-radius: 50%;
	background: #ffffff;
}
.advertisement-action-button.edit img {
	height: 1rem;
}
.advertisement-action-button.save {
	border: 1px solid #21cf47;
}
.advertisement-action-button.cancel {
	border: 1px solid #ff0000;
}

/* Tabs */
.advertisement-settings ::v-deep .nav-tabs .nav-link {
	border: none;
	color: #727272;
}
.advertisement-settings ::v-deep .nav-tabs .nav-link:focus {
	outline: none;
	box-shadow: none;
}
.advertisement-settings ::v-deep .nav-tabs .nav-link.active {
	border-bottom: 2px solid var(--primary-color);
	background: none;
	color: var(--primary-color);
	font-weight: 500;
}

/* Settings fields */
.advertisement-settings-groups {
	display: flex;
	flex-wrap: wrap;
	gap: 1.25rem 2.5rem;
	margin: 1rem 0;
}
.advertisement-settings-group-title {
	display: flex;
	align-items: center;
	gap: 0.35rem;
	margin-bottom: 0.6rem;
	padding-bottom: 0.4rem;
	border-bottom: 1px solid #e6e6e6;
	font-size: 0.7rem;
	font-weight: 600;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: #838383;
}
.advertisement-settings-fields {
	display: flex;
	gap: 1.5rem;
}
.advertisement-settings-field {
	display: flex;
	flex-direction: column;
	gap: 0.2rem;
	width: 6.75rem;
	margin: 0;
	font-size: 0.9375rem;
	font-weight: 400;
	color: #231f20;
}
.advertisement-settings-field-label {
	display: flex;
	align-items: center;
	gap: 0.3rem;
	font-size: 0.75rem;
	font-weight: 600;
	color: #727272;
}
.advertisement-settings-value {
	display: flex;
	align-items: center;
	gap: 0.25rem;
	height: 2rem;
	font-weight: 500;
}
.advertisement-settings-value small,
.advertisement-settings-suffix {
	color: #838383;
	font-size: 0.75rem;
	font-weight: 400;
}
.advertisement-edited-dot {
	width: 0.4rem;
	height: 0.4rem;
	border-radius: 50%;
	background-color: var(--primary-color);
}
.advertisement-settings .form-input {
	width: 100%;
	padding: 0.5rem 0;
	border: 0;
	border-bottom: 1.5px solid #b2b2b2;
	border-radius: 0;
	outline: none;
	box-shadow: none;
	background-color: transparent;
}
.advertisement-settings .form-input:disabled {
	background-color: transparent;
	color: #727272;
}
.advertisement-settings-field .form-input {
	height: 2rem;
	padding: 0;
}
/* Number and unit share one underline */
.advertisement-size-input {
	display: flex;
	align-items: center;
	gap: 0.25rem;
	border-bottom: 1.5px solid #b2b2b2;
}
.advertisement-size-input .form-input {
	min-width: 0;
	border-bottom: 0;
}
.advertisement-size-input select.form-input {
	width: 3.25rem;
	flex-shrink: 0;
	color: #838383;
	font-size: 0.75rem;
}

/* Empty state */
.advertisement-empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 2rem 1rem;
	text-align: center;
}
.advertisement-empty-state-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 4rem;
	height: 4rem;
	margin-bottom: 0.75rem;
	border-radius: 50%;
	background-color: #f3f4f6;
	color: var(--primary-color);
}
.advertisement-empty-state-icon svg {
	width: 2rem;
	height: 2rem;
	fill: none;
	stroke: currentColor;
	stroke-width: 1.5;
	stroke-linecap: round;
	stroke-linejoin: round;
}
.advertisement-empty-state-title {
	margin: 0;
	font-size: 1rem;
	font-weight: 500;
	color: #231f20;
}
.advertisement-empty-state-text {
	margin: 0.25rem 0 0;
	font-size: 0.875rem;
	color: #727272;
}

/* Tiles */
.advertisement-list {
	margin-bottom: 1rem;
}
.advertisement-list-items {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	gap: 1rem;
}
.advertisement-tile {
	width: 12.5rem;
	flex-shrink: 0;
}
.advertisement-tile-thumbnail {
	position: relative;
	height: 7rem;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 1px solid #dcdcdc;
	border-radius: 6px;
	background-color: #ffffff;
	box-shadow: 2px 3px 5px rgba(197, 197, 197, 0.15);
	overflow: hidden;
	color: #727272;
	font-size: 2.5rem;
}
.advertisement-list-items.editing .advertisement-tile-thumbnail {
	cursor: grab;
}
.advertisement-tile-thumbnail > img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}
.advertisement-tile.inactive .advertisement-tile-thumbnail > img,
.advertisement-tile.inactive .advertisement-tile-thumbnail > i {
	opacity: 0.4;
}
.advertisement-tile-badge {
	position: absolute;
	top: 0.375rem;
	left: 0.375rem;
	padding: 0.1rem 0.5rem;
	border-radius: 999px;
	background-color: #231f20;
	color: #ffffff;
	font-size: 0.7rem;
	font-weight: 600;
}
.advertisement-tile-menu {
	position: absolute;
	top: 0.375rem;
	right: 0.375rem;
	display: flex;
	gap: 0.375rem;
}
.advertisement-tile-menu a {
	width: 1.75rem;
	height: 1.75rem;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	background-color: #ffffff;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
	cursor: pointer;
}
.advertisement-tile-menu img {
	width: 0.9rem;
	height: 0.9rem;
}
.advertisement-tile-title {
	margin: 0.5rem 0 0.125rem;
	font-size: 0.875rem;
	font-weight: 500;
	color: #231f20;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.advertisement-tile-footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
	font-size: 0.75rem;
	color: #838383;
}
.advertisement-tile.add-new {
	height: 7rem;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	border: 2px dashed #dcdcdc;
	border-radius: 6px;
	color: #727272;
	font-size: 0.875rem;
	cursor: pointer;
}
.advertisement-tile.add-new i {
	font-size: 2rem;
}

/* Preview */
.advertisement-preview-pickers {
	margin-top: 0;
	padding: 0 0.5rem;
}
.advertisement-preview-page {
	width: 10rem;
}
.advertisement-preview-course {
	min-width: 30rem;
}
.advertisement-settings ::v-deep .v-select.form-input .vs__dropdown-toggle,
.advertisement-settings ::v-deep .v-select.form-input .vs__selected-options,
.advertisement-settings ::v-deep .v-select.form-input .vs__actions {
	padding: 0;
}
.advertisement-settings ::v-deep .v-select.form-input .vs__dropdown-toggle {
	height: 100%;
	align-items: center;
}
.advertisement-settings ::v-deep .v-select.form-input .vs__selected,
.advertisement-settings ::v-deep .v-select.form-input .vs__search {
	margin: 0;
	padding: 0;
	border: 0;
	line-height: normal;
}
.advertisement-preview-frame.with-arrows {
	padding: 0 1.25rem;
}
.advertisement-preview-frame.compact {
	width: 235px;
}
</style>
